// Funnel steps derived from the prospect's backend data and the session (ADR 0002): applicable steps, complete
// steps, first step not complete, and every redirect that follows from them.
import type { Country } from '@/app/country'
import { toCountry } from '@/app/country'
import type { SessionData } from '@/app/store/session'
import type { Contact } from '@/types/contact'

export type StepName =
  | 'project'
  | 'informations'
  | 'legalStatus'
  | 'address'
  | 'mailsOptions'
  | 'formalities'
  | 'finalisation'
  | 'billing'

// Prospect data read by GraphQL, absent until the Contact exists
export interface ProspectData {
  contact:
    | (Pick<Contact, 'email' | 'firstname' | 'lastname' | 'phoneNumber'> & {
        userPresent: boolean
      })
    | null
  society: {
    projectType: string | null
    legalStatus: { id: string } | null
    formality: { id: string } | null
  } | null
  address: { id: string } | null
  currentCart: { status: string; purchases: { category: string }[] } | null
  domiciliation: { paid: boolean } | null
  countryConfig: { countryCode: string } | null
}

export interface NavigationInput {
  prospect?: ProspectData
  session: Pick<
    SessionData,
    'entryParameters' | 'localAnswers' | 'restoreSession'
  >
}

interface StepRules {
  name: StepName
  path: string
  applies: (input: NavigationInput) => boolean
  isComplete: (input: NavigationInput) => boolean
  // Passed over by the next and previous step, while still shown in the breadcrumb
  skipped?: (input: NavigationInput) => boolean
}

export interface ResolvedStep {
  name: StepName
  path: string
  complete: boolean
  skipped: boolean
}

// Where the prospect is sent: a page of the subflow, or the login page of the customer area of a country
export type Destination =
  | { kind: 'page'; path: string }
  | { kind: 'customerAreaLogin'; country: Country }

export interface Navigation {
  // Applicable steps, in the funnel order
  steps: ResolvedStep[]
  firstIncomplete?: ResolvedStep
  paid: boolean
  // Destination of the root of the subflow and of every step the prospect cannot open
  entry: Destination
}

// Path of each step
export const stepPaths: Record<StepName, string> = {
  project: '/project',
  informations: '/informations',
  legalStatus: '/legal-status',
  address: '/address',
  mailsOptions: '/mails-options',
  formalities: '/formalities',
  finalisation: '/payment/finalisation',
  billing: '/payment/billing'
}

// Pages after the funnel steps
export const pagePaths = {
  processing: '/payment/processing',
  welcome: '/welcome',
  error: '/error'
}

// Pages after the payment, without back button nor breadcrumb on desktop
export const pagesAfterPayment = [pagePaths.processing, pagePaths.welcome]

// Purchase categories of the current cart
const cartCategories = ({ prospect }: NavigationInput) =>
  prospect?.currentCart?.purchases.map(({ category }) => category) ?? []

// Project type saved in the backend, else chosen before the Contact exists, else carried by the entry link
export const projectTypeOf = ({
  prospect,
  session
}: NavigationInput): string | undefined =>
  prospect?.society?.projectType ??
  (
    session.localAnswers.projectType ?? session.entryParameters.projectType
  )?.toUpperCase()

// Formality offered by the backend for the project type and the legal status of the society
const formalityOffered = ({ prospect }: NavigationInput) =>
  !!prospect?.society?.formality

// Refusal of the formality given for the current legal status of the society
const formalityRefused = ({ prospect, session }: NavigationInput) =>
  !!session.localAnswers.formalityRefusedFor &&
  session.localAnswers.formalityRefusedFor ===
    prospect?.society?.legalStatus?.id

// Restore session of a cart still in completion: the formality was decided before the prospect left, for the
// legal status kept since
const restoredCartInCompletion = ({ prospect, session }: NavigationInput) =>
  session.restoreSession &&
  !session.localAnswers.legalStatusChanged &&
  prospect?.currentCart?.status === 'in_completion'

// Rule of a step that applies to every prospect
const always = () => true

const steps: StepRules[] = [
  {
    name: 'project',
    path: stepPaths.project,
    applies: always,
    isComplete: (input) => !!projectTypeOf(input)
  },
  {
    name: 'informations',
    path: stepPaths.informations,
    applies: always,
    isComplete: ({ prospect }) =>
      !!prospect?.contact?.email &&
      !!prospect.contact.phoneNumber &&
      !!prospect.contact.firstname &&
      !!prospect.contact.lastname
  },
  {
    name: 'legalStatus',
    path: stepPaths.legalStatus,
    applies: always,
    isComplete: ({ prospect }) => !!prospect?.society?.legalStatus,
    // Legal status of the entry link, saved with the Contact
    skipped: ({ session }) => !!session.entryParameters.legalStatusId
  },
  {
    name: 'address',
    path: stepPaths.address,
    applies: always,
    isComplete: ({ prospect }) => !!prospect?.address
  },
  {
    name: 'mailsOptions',
    path: stepPaths.mailsOptions,
    applies: always,
    isComplete: (input) => cartCategories(input).includes('mail')
  },
  {
    name: 'formalities',
    path: stepPaths.formalities,
    applies: (input) =>
      formalityOffered(input) && !input.session.entryParameters.partnerDeal,
    isComplete: (input) =>
      cartCategories(input).includes('formality') ||
      formalityRefused(input) ||
      restoredCartInCompletion(input)
  },
  {
    name: 'finalisation',
    path: stepPaths.finalisation,
    applies: always,
    isComplete: ({ session }) => !!session.localAnswers.finalisationValidated
  },
  {
    name: 'billing',
    path: stepPaths.billing,
    applies: always,
    isComplete: ({ prospect }) => !!prospect?.domiciliation?.paid
  }
]

// Paid domiciliation: welcome page, or login page of the customer area when the customer account exists
const paidDestination = ({ prospect }: NavigationInput): Destination => {
  const country = toCountry(prospect?.countryConfig?.countryCode)
  if (prospect?.contact?.userPresent && country)
    return { kind: 'customerAreaLogin', country }
  return { kind: 'page', path: pagePaths.welcome }
}

// Applicable steps of the prospect, their completeness, the first one not complete and the entry destination
export const resolveSteps = (input: NavigationInput): Navigation => {
  const applicableSteps = steps
    .filter((step) => step.applies(input))
    .map(({ name, path, isComplete, skipped }) => ({
      name,
      path,
      complete: isComplete(input),
      skipped: skipped?.(input) ?? false
    }))
  const firstIncomplete = applicableSteps.find(({ complete }) => !complete)
  const paid = !!input.prospect?.domiciliation?.paid

  return {
    steps: applicableSteps,
    firstIncomplete,
    paid,
    entry: paid
      ? paidDestination(input)
      : {
          kind: 'page',
          path: (firstIncomplete ?? applicableSteps.at(-1)!).path
        }
  }
}

// Position of a step path among the applicable steps, -1 when it is not one
export const stepIndex = ({ steps }: Navigation, path: string) =>
  steps.findIndex((step) => step.path === path)

// Whether the prospect can open a step: applicable, not after the first step not complete, domiciliation not paid
export const canOpenStep = (navigation: Navigation, path: string) => {
  const index = stepIndex(navigation, path)
  if (navigation.paid || index === -1) return false
  const { firstIncomplete } = navigation
  return (
    !firstIncomplete || index <= stepIndex(navigation, firstIncomplete.path)
  )
}

export type BreadcrumbState = 'done' | 'current' | 'upcoming'

export interface BreadcrumbStep extends ResolvedStep {
  state: BreadcrumbState
  openable: boolean
}

// State of a step in the breadcrumb: the current one, else done once complete
const breadcrumbState = (step: ResolvedStep, path: string): BreadcrumbState => {
  if (step.path === path) return 'current'
  return step.complete ? 'done' : 'upcoming'
}

// Applicable steps shown in the breadcrumb from the current path, each one with its state and whether it can be opened
export const breadcrumbItems = (
  navigation: Navigation,
  path: string
): BreadcrumbStep[] =>
  navigation.steps.map((step) => ({
    ...step,
    state: breadcrumbState(step, path),
    openable: canOpenStep(navigation, step.path)
  }))

// Step that the subflow opens instead of the path, undefined when the path can be opened
export const redirectFor = (
  navigation: Navigation,
  path: string
): Destination | undefined => {
  if (path === '/') return navigation.entry
  if (!steps.some((step) => step.path === path)) return undefined
  return canOpenStep(navigation, path) ? undefined : navigation.entry
}

// Next applicable step not skipped, undefined after the billing step
export const nextStepPath = (navigation: Navigation, path: string) => {
  const index = stepIndex(navigation, path)
  if (index === -1) return undefined
  return navigation.steps.slice(index + 1).find(({ skipped }) => !skipped)?.path
}

// Previous applicable step not skipped; the processing page goes back to the billing step
export const previousStepPath = (navigation: Navigation, path: string) => {
  if (path === pagePaths.processing) return navigation.steps.at(-1)?.path
  const index = stepIndex(navigation, path)
  if (index === -1) return undefined
  return navigation.steps.slice(0, index).findLast(({ skipped }) => !skipped)
    ?.path
}
